var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/**
 * [CLAIMS_CONTEXT]
 * purpose: Displays and updates equipment information along with its supporting receipt and picture documents.
 * touches: equipment/claim/qualification/settings store, equipment validation, insurer feature flags for pictures/justificatives.
 * invariants: equipment fields update through the current damage change; required picture count depends on claim type and settings; validity is true when the count is reached or none is required.
 * inputs: equipment damage data, claim type, insurer settings, uploaded receipt/picture files.
 * outputs: renders the equipment form, updates equipment data, emits picture validation state to the parent.
 */
import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { UiCard } from '@claims-ia/design-system';
import AmountInput from '@declaration/components/UI/AmountInput.vue';
import RadioPrimaryButton from '@declaration/components/UI/RadioPrimaryButton.vue';
import ReceiptUpload from '@declaration/components/UI/ReceiptUpload.vue';
import FileCollectionUpload from '@declaration/components/upload/FileCollectionUpload.vue';
import PictureCollectionUpload from '@declaration/components/upload/PictureCollectionUpload.vue';
import { YES_OR_NO_CHOICES } from '@declaration/helpers/choices';
import AgeInput from '@declaration/components/AgeInput.vue';
import { ClaimFileType } from '@shared/types/file';
import { InsurerFeatureFlag, insurerHasFeature } from '@shared/featureFlags/insurerFeatureFlags';
const claim = namespace('claim');
const equipment = namespace('equipment');
const settings = namespace('settings');
const qualification = namespace('qualification');
let EquipmentForm = class EquipmentForm extends Vue {
    constructor() {
        super(...arguments);
        this.yesNoQuestionValuesAndTradKeys = YES_OR_NO_CHOICES;
        this.isRepairQuoteMandatory = null;
        this.ClaimFileType = ClaimFileType;
    }
    onRequiredPhotosChanged() {
        this.$emit('update:photos-valid', this.hasEnoughPhotos);
    }
    onPictureCollectionChanged() {
        this.$emit('update:photos-valid', this.hasEnoughPhotos);
    }
    get change() {
        return this.changeGetter(this.changeKey);
    }
    get equipment() {
        return this.change.data;
    }
    get validation() {
        return this.validationGetter(this.changeKey);
    }
    get hasDisableDamagesPictures() {
        return insurerHasFeature(InsurerFeatureFlag.DisableDamagesPictures);
    }
    get hasDisableDamagesJustificatives() {
        return insurerHasFeature(InsurerFeatureFlag.DisableDamagesJustificatives);
    }
    get requiredPhotos() {
        if (!this.trigram || !this.setting)
            return 0;
        let value = null;
        try {
            const grouped = this.setting('damageRequiredPhotosCount');
            if (grouped && typeof grouped === 'object') {
                value = grouped[this.trigram];
            }
        }
        catch (_a) { }
        const count = Number(value);
        return isNaN(count) ? 0 : count;
    }
    get hasEnoughPhotos() {
        var _a, _b;
        if (this.requiredPhotos === 0)
            return true;
        const collection = this.equipment.pictureCollection;
        if (!collection || typeof collection === 'string')
            return false;
        return ((_b = (_a = collection.uploadedAppFiles) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) >= this.requiredPhotos;
    }
    onPictureCollectionUpdate(pictureCollection) {
        this.updateEquipment({ changeKey: this.changeKey, data: { pictureCollection } });
        this.$nextTick(() => this.$emit('update:photos-valid', this.hasEnoughPhotos));
    }
    update(data) {
        this.updateEquipment({ changeKey: this.changeKey, data });
    }
};
__decorate([
    claim.State('claim')
], EquipmentForm.prototype, "claim", void 0);
__decorate([
    equipment.Getter('change')
], EquipmentForm.prototype, "changeGetter", void 0);
__decorate([
    equipment.Getter('validation')
], EquipmentForm.prototype, "validationGetter", void 0);
__decorate([
    equipment.Action('updateDamage')
], EquipmentForm.prototype, "updateEquipment", void 0);
__decorate([
    settings.Getter('setting')
], EquipmentForm.prototype, "setting", void 0);
__decorate([
    qualification.Getter('typeTrigram')
], EquipmentForm.prototype, "trigram", void 0);
__decorate([
    Watch('requiredPhotos', { immediate: true })
], EquipmentForm.prototype, "onRequiredPhotosChanged", null);
__decorate([
    Watch('equipment.pictureCollection', { deep: true, immediate: true })
], EquipmentForm.prototype, "onPictureCollectionChanged", null);
EquipmentForm = __decorate([
    Component({
        components: {
            FileCollectionUpload,
            PictureCollectionUpload,
            AmountInput,
            AgeInput,
            ReceiptUpload,
            RadioPrimaryButton,
            UiCard,
        },
        props: {
            changeKey: { type: [String, Number], default: null },
            validateRequested: { type: Boolean, default: false },
        },
    })
], EquipmentForm);
export default EquipmentForm;
